// DescEditor.jsx — CKEditor 4 (CDN) вместо textarea
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import s from "../modules/DescEditor.module.css";
import { useT, useI18nReady } from "../components/TextProvider";

const TUSHUNCHALAR_ENDPOINT =
  process.env.REACT_APP_TUSHUNCHALAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/tushunchalar/";
const VAZIFALAR_ENDPOINT =
  process.env.REACT_APP_VAZIFALAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/vazifalar/";

/* ===== helpers ===== */
function getAnyTokenFromStorage() {
  const pick = (st) =>
    st.getItem("auth:access") ||
    st.getItem("auth:token") ||
    st.getItem("auth:key") ||
    st.getItem("auth:jwt") ||
    st.getItem("auth:auth_token") ||
    null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}
function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forced = process.env.REACT_APP_AUTH_SCHEME;
  if (forced) return { Authorization: `${forced} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}
async function fetchJSON(url, options = {}) {
  const res = await fetch(url, options);
  let data = null;
  try { data = await res.json(); } catch {}
  return { ok: res.ok, status: res.status, data };
}
function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return (tmp.textContent || tmp.innerText || "").trim();
}

/* ===== CKEditor 4 loader & config (как в TrainerCreate) ===== */
function ensureCKEScript() {
  return new Promise((resolve, reject) => {
    if (window.CKEDITOR) return resolve();
    const script = document.createElement("script");
    script.src = "https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js";
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });
}
const commonCkeConfig = {
    language: "uz",
    height: 520,                // базовая высота побольше
    resize_enabled: true,
    resize_minHeight: 320,  
  contentsLangDirection: "ltr",
  contentsCss: [
    "https://cdn.ckeditor.com/4.22.1/full-all/contents.css",
    "body { direction: ltr; text-align: left; }",
  ],
  toolbar: [
    { name: "document", items: ["Source","-","NewPage","Preview","Print"] },
    { name: "clipboard", items: ["Cut","Copy","Paste","PasteText","PasteFromWord","-","Undo","Redo"] },
    { name: "editing",  items: ["Find","Replace","SelectAll","Scayt"] },
    { name: "forms",    items: ["Checkbox","Radio","TextField","Textarea","Select","Button","ImageButton","HiddenField"] },
    "/",
    { name: "basicstyles", items: ["Bold","Italic","Underline","Strike","Subscript","Superscript","-","RemoveFormat"] },
    { name: "paragraph",   items: ["NumberedList","BulletedList","-","Outdent","Indent","-","Blockquote","CreateDiv","-","JustifyLeft","JustifyCenter","JustifyRight","JustifyBlock","-","BidiLtr","BidiRtl","Language"] },
    { name: "links",       items: ["Link","Unlink","Anchor"] },
    { name: "insert",      items: ["Image","Table","HorizontalRule","Smiley","SpecialChar","PageBreak","Iframe"] },
    "/",
    { name: "styles", items: ["Styles","Format","Font","FontSize"] },
    { name: "colors", items: ["TextColor","BGColor"] },
    { name: "tools",  items: ["Maximize","ShowBlocks"] },
    { name: "about",  items: ["About"] },
  ],
  extraPlugins: "colorbutton,font,justify,language,bidi,div,find,smiley,iframe",
  removePlugins: "elementspath,notification",
  resize_enabled: true,
};

export default function DescEditor() {

  const t = useT();
  const ready = useI18nReady();

  const { id } = useParams(); // id записи tushunchalar
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");

  const [matni, setMatni]       = useState("");       // HTML контент
  const [vazifaId, setVazifaId] = useState(null);
  const [vazifa, setVazifa]     = useState(null);

  const originalMatniRef = useRef(""); // для dirty check

  const token = useMemo(() => getAnyTokenFromStorage(), []);
  const editorRef = useRef(null);
  const editorId  = useMemo(() => "desc-editor-" + Math.random().toString(36).slice(2), []);

  // ===== загрузка записи + задания =====
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { ok, status, data } = await fetchJSON(`${TUSHUNCHALAR_ENDPOINT}${id}/`, {
          headers: { ...makeAuthHeader(token) },
          credentials: "include",
          signal: ac.signal,
        });
        if (!ok) {
          setError(status === 404 ? "Tavsif topilmadi" : "Tavsifni yuklashda xatolik yuz berdi");
          setLoading(false);
          return;
        }
        const html = data?.matni ?? "";
        setMatni(html);
        originalMatniRef.current = html;

        const vid = data?.vazifa ?? null;
        setVazifaId(vid);
        if (vid != null) {
          const r2 = await fetchJSON(`${VAZIFALAR_ENDPOINT}${vid}/`, { signal: ac.signal });
          if (r2.ok) setVazifa(r2.data);
        }
      } catch {
        if (!ac.signal.aborted) setError("Yuklash paytida tarmoq xatosi");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [id, token]);

  // ===== инициализация CKEditor после загрузки скрипта и DOM textarea =====
  // ИНИЦИАЛИЗАЦИЯ CKEditor — ждём окончания загрузки и наличие textarea
useEffect(() => {
    if (loading) return; // редактор появляется в разметке только после загрузки
  
    let destroyed = false;
  
    (async () => {
      await ensureCKEScript();
      if (destroyed) return;
  
      const el = document.getElementById(editorId);
      if (!el) return; // textarea ещё не в DOM — просто выходим
  
      // убираем старый инстанс, если есть (StrictMode двойной mount)
      if (window.CKEDITOR?.instances?.[editorId]) {
        window.CKEDITOR.instances[editorId].destroy(true);
      }
  
      // важный момент: можно передать сам элемент, а не строковый id
      const inst = window.CKEDITOR.replace(el, commonCkeConfig);
      if (!inst) return; // если по какой-то причине вернулся null — не продолжаем
  
      editorRef.current = inst;
  
      inst.on("instanceReady", () => {
        try { inst.resize("100%", 260); } catch {}
        // ставим контент, который пришёл с сервера
        inst.setData(matni || "");
      });
  
      inst.on("change", () => {
        // синхронизация state ← CKEditor
        setMatni(inst.getData());
      });
    })();
  
    return () => {
      destroyed = true;
      const exists = window.CKEDITOR?.instances?.[editorId];
      if (exists) exists.destroy(true);
    };
    // теперь эффект зависит и от loading, и от editorId
  }, [editorId, loading]);
  
  // Если matni обновился из вне (после фетча), а редактор уже создан — подольём в него
  useEffect(() => {
    const inst = editorRef.current;
    if (!inst || !inst.getData) return;
  
    // чтобы не зациклиться: обновляем только если данные реально отличаются
    if (matni !== inst.getData()) {
      inst.setData(matni || "");
    }
  }, [matni]);
  

  // если matni пришёл позже, обновим редактор
  useEffect(() => {
    const inst = editorRef.current;
    if (inst && inst.getData && matni !== inst.getData()) {
      inst.setData(matni || "");
    }
  }, [matni]);

  // ===== предупреждение об изменениях =====
  const dirty = useMemo(() => matni !== originalMatniRef.current, [matni]);
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // ===== Ctrl+S → сохранить =====
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [matni]);

  async function handleSave() {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { ok, status, data } = await fetchJSON(`${TUSHUNCHALAR_ENDPOINT}${id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...makeAuthHeader(token),
        },
        credentials: "include",
        body: JSON.stringify({ matni }), // HTML из CKEditor
      });
      if (!ok) {
        setError(status === 400 ? "Forma maydonlarini tekshiring" : "O'zgarishlar saqlanmadi");
        return;
      }
      originalMatniRef.current = data?.matni ?? matni;
      setSuccess("Saqlandi");
      setTimeout(() => setSuccess(""), 1200);
    } catch {
      setError("Saqlash paytida tarmoq xatosi");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm("Удалить описание навсегда?")) return;
    try {
      const { ok } = await fetchJSON(`${TUSHUNCHALAR_ENDPOINT}${id}/`, {
        method: "DELETE",
        headers: { ...makeAuthHeader(token) },
        credentials: "include",
      });
      if (ok) navigate("/trainer/my-descriptions");
      else setError("Не удалось удалить описание");
    } catch {
      setError("Ошибка сети при удалении");
    }
  }

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <div>
          <h1 className={s.title}>{t('desc2Red')}</h1>
          <div className={s.subtitle}>
            {vazifa?.name ? `Mashq: ${vazifa.name}` :
              (vazifaId != null ? `Mashq #${vazifaId}` : "Mashq nomalum")}
          </div>
        </div>
        <div className={s.headerActions}>
          <Link to="/trainer/my-descriptions" className={s.btnGhost}>← {t('ruyxatgaQaytish')}</Link>
          {/* <button className={s.btnDanger} onClick={handleDelete}>Удалить</button> */}
          <button className={s.btnPrimary} onClick={handleSave} disabled={saving || !dirty}>
            {saving ? "Saqlanmoqda…" : (dirty ? "Saqlash" : "Saqlandi")}
          </button>
        </div>
      </div>

      {error && <div className={s.error}>{error}</div>}
      {success && <div className={s.success}>{success}</div>}

      {loading ? (
        <div className={s.loading}>Yulkanmoqda...</div>
      ) : (
        <div className={s.editorGrid}>
          <div className={s.editorPane}>
            <label className={s.label}>{t('Tavsif')}</label>
            {/* CKEditor 4 заменит этот textarea по id */}
            <textarea id={editorId} defaultValue={matni} />
            
          </div>    
        </div>
      )}
    </div>
  );
}
